
import { sncBillingError } from "@/helpers/sncBillingError";
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import { debounce } from "lodash";
import { format } from "date-fns";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle";
import {
  shiftsCancelledBilling,
  clearShiftsCancelledBilling
} from "@/api";
import _ from "lodash";
import { constants } from "@/utils/constants";

export default Vue.extend({
  name: "SNCBillingBoard",
  components: {
    TempStarsTableTitle
  },
  data: () => {
    return {
      alert: false,
      alertMessage: "",
      noteModal: false,
      notes: "",
      noteTitle: "",
      itemID: 0,
      locationId: 0,
      hygienistId: 0,
      jobId: 0,
      callBack: () => {},
      success: false,
      tableSearch: "",
      loading: false,
      allData: [] as any[],
      options: {
        page: 1,
        itemsPerPage: 15,
        sortBy: [],
        sortDesc: []
      } as any,
      detailsDialog: false,
      detailsDialogContent: "",
      viewInfoModal: false,
      viewInfoItem: null as any,
      viewInfoNotes: "",
      viewInfoConfirmAction: "" as "process" | "skip" | "clear" | "",
      viewInfoLoading: false,
      confirmDialog: false
    };
  },
  computed: {
    ...mapState({
      content: "tableContent"
    }),
    tableHeaders() {
      const tableColumns = constants.enabledColumns.sncBillingBoardTable;
      const data = tableColumns.map(i => {
        if (i === "rebookedLocationId") {
          return { align: "left", text: "Rebooked LOC ID", value: i };
        }
        return { align: "left", text: _.startCase(i), value: i };
      });
      return [
        ...data,
        { align: "left", text: "Booked By", value: "bookedBy" },
        { align: "center", text: "Actions", value: "actions", sortable: false, width: "170px" }
      ];
    },
    confirmActionLabel(): string {
      const map: Record<string, string> = { process: "Process Payment", skip: "Skip (Waive)", clear: "Clear / No Charge" };
      return map[this.viewInfoConfirmAction] || "";
    },
    confirmActionColor(): string {
      const map: Record<string, string> = { process: "green", skip: "amber darken-2", clear: "#FB7672" };
      return map[this.viewInfoConfirmAction] || "primary";
    },
    filteredItems(): any[] {
      if (!this.tableSearch || this.tableSearch.trim() === "") {
        return this.allData;
      }
      const searchTerm = this.tableSearch.toLowerCase().trim();
      return this.allData.filter(item => {
        if (item.id && String(item.id).includes(searchTerm)) return true;
        if (item.jobId && String(item.jobId).includes(searchTerm)) return true;
        if (item.hygienistId && String(item.hygienistId).includes(searchTerm)) return true;
        if (item.locationId && String(item.locationId).includes(searchTerm)) return true;
        if (item.dentistId && String(item.dentistId).includes(searchTerm)) return true;
        if (item.firstName && item.firstName.toLowerCase().includes(searchTerm)) return true;
        if (item.lastName && item.lastName.toLowerCase().includes(searchTerm)) return true;
        if (item.practiceName && item.practiceName.toLowerCase().includes(searchTerm)) return true;
        if (item.email && item.email.toLowerCase().includes(searchTerm)) return true;
        return false;
      });
    },
    totalItems(): number {
      return this.filteredItems.length;
    },
    paginatedItems(): any[] {
      const start = (this.options.page - 1) * this.options.itemsPerPage;
      const end = start + this.options.itemsPerPage;
      return this.filteredItems.slice(start, end);
    }
  },
  methods: {
    ...mapMutations(["modify", "callSnackbar"]),
    async loadData(): Promise<void> {
      this.loading = true;
      try {
        await this.$store.dispatch("getData", {
          tablename: this.$options.name
        });
        this.allData = [...this.content];
      } catch (error) {
        this.callSnackbar({
          visible: true,
          text: error instanceof Error ? error.message : "Error loading data",
          color: "red darken-2"
        });
      } finally {
        this.loading = false;
      }
    },
    async process(): Promise<void> {
      let response: Response | undefined;
      try {
        response = await shiftsCancelledBilling({
          id: this.itemID,
          notes: this.notes
        });
        if (response && response.status >= 400) throw new Error();
        await this.loadData();
        this.alertMessage =
          format(new Date(), "MMMM do yyyy, HH:mm") + " Payment Successful";
        this.success = true;
      } catch (err) {
        this.alertMessage =
          format(new Date(), "MMMM do yyyy, HH:mm") +
          `\n${await sncBillingError(response)}`;
        this.success = false;
      } finally {
        this.alert = true;
      }
    },

    async clear(): Promise<void> {
      let response: Response | undefined;
      try {
        response = await clearShiftsCancelledBilling({
          id: this.itemID,
          hygienistId: this.hygienistId,
          jobId: this.jobId,
          locationId: this.locationId,
          notes: this.notes
        });
        if (response && response.status >= 400) throw new Error();
        await this.loadData();
        this.alertMessage =
          format(new Date(), "MMMM do yyyy, HH:mm") + " Success";
        this.success = true;
      } catch (err) {
        this.alertMessage =
          format(new Date(), "MMMM do yyyy, HH:mm") +
          `\n${await sncBillingError(response)}`;
        this.success = false;
      } finally {
        this.alert = true;
      }
    },

    openNoteModal(callBack: () => void, item: any) {
      this.noteModal = true;
      this.itemID = item.id;
      this.hygienistId = item.hygienistId;
      this.locationId = item.locationId;
      this.jobId = item.jobId;
      this.callBack = callBack;
    },

    openViewInfo(item: any) {
      this.viewInfoItem = { ...item };
      this.viewInfoNotes = "";
      this.viewInfoConfirmAction = "";
      this.viewInfoModal = true;
    },

    async executeViewInfoAction(): Promise<void> {
      if (!this.viewInfoItem || !this.viewInfoNotes.trim()) return;
      this.viewInfoLoading = true;
      let response: Response | undefined;
      try {
        const item = this.viewInfoItem;
        if (this.viewInfoConfirmAction === "process") {
          response = await shiftsCancelledBilling({
            id: item.id,
            notes: this.viewInfoNotes
          });
        } else if (this.viewInfoConfirmAction === "skip") {
          response = await shiftsCancelledBilling({
            id: item.id,
            notes: this.viewInfoNotes,
            skipPayment: true
          });
        } else if (this.viewInfoConfirmAction === "clear") {
          response = await clearShiftsCancelledBilling({
            id: item.id,
            hygienistId: item.hygienistId,
            jobId: item.jobId,
            locationId: item.locationId,
            notes: this.viewInfoNotes
          });
        }
        if (response && response.status >= 400) throw new Error();
        this.confirmDialog = false;
        this.viewInfoModal = false;
        await this.loadData();
        this.alertMessage =
          format(new Date(), "MMMM do yyyy, HH:mm") + ` ${this.confirmActionLabel} — Success`;
        this.success = true;
        this.alert = true;
      } catch (err) {
        this.confirmDialog = false;
        this.alertMessage =
          format(new Date(), "MMMM do yyyy, HH:mm") +
          `\n${await sncBillingError(response)}`;
        this.success = false;
        this.alert = true;
      } finally {
        this.viewInfoLoading = false;
      }
    },

    formatShiftDate(dateString: string): string {
      if (!dateString) return "-";
      try {
        const date = new Date(dateString.slice(0, -5) || dateString);
        return format(date, "MMMM do yyyy");
      } catch (error) {
        return dateString;
      }
    },

    openDetailsDialog(text: string) {
      this.detailsDialogContent = text;
      this.detailsDialog = true;
    },

    formatReason(reason: string): string {
      if (!reason) return "-";
      const map: Record<string, string> = {
        patientsCancelled: "Patients Cancelled",
        permanentStaffCovered: "Staff Covered",
        other: "Other",
        officeEmergency: "Office Emergency",
        noLongerNeeded: "No Longer Needed"
      };
      return map[reason] || reason;
    },

    getCancelReasonColor(reason: string): string {
      if (reason === "patientsCancelled") return "#FB7672";
      if (reason === "permanentStaffCovered") return "#0B4598";
      if (reason === "other") return "#757575";
      return "#FB7672";
    },

    bookedBy(item: any): string {
      if (!item.Instabook && !item.Autobook) {
        if (item.partialOfferId) {
          return "Submitted Offer";
        }
        return "Manually Booked";
      } else if (item.Instabook && !item.Autobook) {
        return "Instabooked by talent";
      } else if (!item.Instabook && item.Autobook) {
        return "Autobooked by office";
      } else {
        return "Instabooked/Autobooked";
      }
    }
  },
  watch: {
    content: {
      handler(newContent: any[]) {
        if (Array.isArray(newContent) && newContent.length > 0) {
          this.allData = [...newContent];
        }
      },
      immediate: true
    },
    tableSearch: debounce(function(this: any) {
      if (this.options) {
        this.options.page = 1;
      }
    }, 300)
  },
  async mounted() {
    await this.loadData();
  }
});
